import { am5, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { LABEL_FONT_SIZE } from './calculate-label-width';
import { COLOR_ERROR, COLOR_SUCCESS } from '../../../../theme/layout-size';

export const createSeries = ({
  root,
  chart,
  data,
  xAxisField,
  xAxisRenderer,
  yAxisRenderer,
  showEndValueLabel,
  dotted,
  seriesColorByName,
  colorizeBySign = false,
  isLargeDataset,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  data: types.widgets.TableData;
  xAxisField: string;
  xAxisRenderer: am5xy.ValueAxis<am5xy.AxisRenderer>;
  yAxisRenderer: am5xy.ValueAxis<am5xy.AxisRenderer>;
  showEndValueLabel: boolean;
  dotted: string[] | undefined;
  seriesColorByName: Record<string, string>;
  colorizeBySign?: boolean;
  isLargeDataset: boolean;
}) => {
  const GREEN = am5.Color.fromString(COLOR_SUCCESS);
  const RED = am5.Color.fromString(COLOR_ERROR);

  const makeSeries = (field: string, color: am5.Color) => {
    const s = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: field,
        xAxis: xAxisRenderer,
        yAxis: yAxisRenderer,
        valueYField: field,
        valueXField: xAxisField,
        stroke: color,
        fill: color,
        tooltip: am5.Tooltip.new(root, { labelText: '[bold]{name}[/]: {valueY}' }),
        minDistance: isLargeDataset ? 0.2 : 0,
      })
    );

    if (colorizeBySign) {
      s.strokes.template.setAll({ templateField: `${field}__strokeStyle` });
      s.fills.template.setAll({ templateField: `${field}__fillStyle` });
    }

    if (dotted?.includes(field)) {
      s.strokes.template.set('strokeDasharray', [3, 3]);
    }

    s.bullets.clear();
    if (showEndValueLabel) {
      s.bullets.push((root, series, dataItem) => {
        const lastIndex = series.dataItems.length - 1;
        if (series.dataItems.indexOf(dataItem) !== lastIndex) return null;
        const v = (dataItem.dataContext as any)[field] as number;
        const c = colorizeBySign ? (v >= 0 ? GREEN : RED) : color;
        const formatted = root.numberFormatter.format(v);
        const cx = Math.min(50, Math.max(0, formatted.length * 2));
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: ` ${formatted}`,
            fontSize: LABEL_FONT_SIZE,
            fill: am5.color('#000000'),
            background: am5.Rectangle.new(root, { fill: c, fillOpacity: 1 }),
            centerY: am5.p50,
            centerX: am5.percent(cx),
            paddingLeft: 2,
            paddingRight: 5,
            paddingTop: 2,
            paddingBottom: 2,
          }),
        });
      });
    }

    s.get('tooltip')?.label.setAll({ fontSize: 11 });
    return s;
  };

  const keys = Object.keys(data?.[0] ?? {}).filter((key) => key !== xAxisField);

  const series = keys.map((key) => {
    const hex = seriesColorByName[key];
    const base = am5.color(hex);

    const styled = colorizeBySign
      ? data.map((r) => {
          const v = r[key] as number | null | undefined;
          const col = typeof v === 'number' && v < 0 ? RED : GREEN;
          return {
            ...r,
            [`${key}__strokeStyle`]: { stroke: col },
            [`${key}__fillStyle`]: { fill: col },
          };
        })
      : data;

    const s = makeSeries(key, base);
    s.data.setAll(styled);
    return s;
  });

  return series;
};
